import { resolveOperationBundle } from './operation-bundles/index.js';
import { CANONICAL_OPERATIONS_REGISTRY } from '../operations/index.js';
import { EFFECTIVE_PERMISSION_REGISTRY, PLANNED_ONLY_PERMISSION_IDS, } from './effective-permission.registry.js';
import { INVESTMENT_DOMAIN_MODULES } from '../modules/domain-bundles.js';
import { PLATFORM_ROOT_ADMIN_BASELINE_MODULES } from './personas/presets.js';
const PERMISSION_BUNDLE_OPERATION_BUNDLE_MAP = {
    'platform.root': 'platform-root',
    'platform.admin': 'platform-admin',
    'platform.user': 'platform-read',
    'organization.admin': 'organization-admin',
    'organization.user.base': 'organization-user-base',
    'organization.client.base': 'client-self-service',
};
const PLATFORM_ADMIN_PERMISSION_PREFIXES = [
    'platform:',
    'settings:',
    'auth:',
    'navigation:',
    'admin:hub',
    'access:permissions:read',
    'organization:market-data:',
    'organization:instruments:',
];
const PERMISSION_BUNDLE_ALLOWED_PREFIXES = {
    'platform.root': PLATFORM_ADMIN_PERMISSION_PREFIXES,
    'platform.admin': PLATFORM_ADMIN_PERMISSION_PREFIXES,
    'platform.user': ['platform:', 'settings:', 'auth:', 'navigation:'],
    'organization.admin': [
        'organization:',
        'accounting:',
        'data:hub',
        'admin:hub',
        'navigation:read',
        'auth:',
        'settings:',
    ],
    'organization.user.base': ['organization:', 'data:hub', 'settings:', 'auth:', 'navigation:'],
    'organization.client.base': ['client:', 'data:hub', 'navigation:read', 'settings:', 'auth:'],
};
function permissionMatchesBundle(permission, bundleId) {
    if (PLANNED_ONLY_PERMISSION_IDS.has(permission))
        return false;
    const allowedPrefixes = PERMISSION_BUNDLE_ALLOWED_PREFIXES[bundleId];
    if (!allowedPrefixes)
        return true;
    return allowedPrefixes.some((prefix) => permission === prefix || permission.startsWith(prefix));
}
function getPermissionIdsForOperationBundle(operationBundleId) {
    const bundle = resolveOperationBundle(operationBundleId);
    const ids = new Set();
    for (const operationId of bundle.operationIds) {
        const descriptor = CANONICAL_OPERATIONS_REGISTRY[operationId];
        const permission = (descriptor?.permissionId ?? descriptor?.permission);
        if (permission &&
            permission in EFFECTIVE_PERMISSION_REGISTRY &&
            !PLANNED_ONLY_PERMISSION_IDS.has(permission)) {
            ids.add(permission);
        }
    }
    return Array.from(ids);
}
function generatePermissionIdsForBundle(bundleId) {
    const operationBundleId = PERMISSION_BUNDLE_OPERATION_BUNDLE_MAP[bundleId];
    if (!operationBundleId)
        return [];
    const operationPermissions = getPermissionIdsForOperationBundle(operationBundleId).filter((permission) => permissionMatchesBundle(permission, bundleId));
    // Organization permission ceilings are derived only from active operations.
    // Accounting report-read permissions are allowed because the V1 report
    // catalog exposes governed accounting information, while accounting mutation
    // operations remain absent from the operation bundle.
    if (bundleId === 'organization.admin') {
        const resolved = new Set(operationPermissions);
        resolved.add('organization:users:email:view');
        resolved.add('access:permissions:read');
        return Array.from(resolved);
    }
    // Platform administrator bundles intentionally include non-route platform
    // permissions used by bootstrap, recovery, and operational tooling.
    if (bundleId === 'platform.root' || bundleId === 'platform.admin') {
        const resolved = new Set(operationPermissions);
        for (const permission of Object.keys(EFFECTIVE_PERMISSION_REGISTRY)) {
            if (permissionMatchesBundle(permission, bundleId))
                resolved.add(permission);
        }
        return Array.from(resolved);
    }
    return operationPermissions;
}
export const PERMISSION_BUNDLE_REGISTRY = {
    'platform.root': {
        id: 'platform.root',
        name: 'Platform Root',
        persona: 'PLATFORM_ROOT',
        permissionIds: generatePermissionIdsForBundle('platform.root'),
        requiredModules: PLATFORM_ROOT_ADMIN_BASELINE_MODULES,
    },
    'platform.admin': {
        id: 'platform.admin',
        name: 'Platform Admin',
        persona: 'PLATFORM_ADMIN',
        permissionIds: generatePermissionIdsForBundle('platform.admin'),
        requiredModules: PLATFORM_ROOT_ADMIN_BASELINE_MODULES,
    },
    'platform.user': {
        id: 'platform.user',
        name: 'Platform User',
        persona: 'PLATFORM_USER',
        permissionIds: generatePermissionIdsForBundle('platform.user'),
        requiredModules: ['PLATFORM', 'NAVIGATION', 'DASHBOARD'],
    },
    'organization.admin': {
        id: 'organization.admin',
        name: 'Organization Admin',
        persona: 'ORG_ADMIN',
        permissionIds: generatePermissionIdsForBundle('organization.admin'),
        requiredModules: INVESTMENT_DOMAIN_MODULES,
    },
    'organization.user.base': {
        id: 'organization.user.base',
        name: 'Organization User Base',
        persona: 'ORG_USER',
        permissionIds: generatePermissionIdsForBundle('organization.user.base'),
        requiredModules: ['INVESTMENT', 'REPORTING', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.data-hub-reader': {
        id: 'organization.user.data-hub-reader',
        name: 'Organization User Data Hub Reader',
        persona: 'ORG_USER',
        permissionIds: ['data:hub', 'organization:dashboard:read'],
        requiredModules: ['DATA', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.transactions-operator': {
        id: 'organization.user.transactions-operator',
        name: 'Organization User Transactions Operator',
        persona: 'ORG_USER',
        permissionIds: [
            'organization:transactions:create',
            'organization:transactions:update',
            'organization:transactions:submit',
            'organization:transactions:withdraw',
            'organization:transactions:repair',
            'organization:transactions:repost',
        ],
        requiredModules: ['INVESTMENT', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.transactions-approver': {
        id: 'organization.user.transactions-approver',
        name: 'Organization User Transactions Approver',
        persona: 'ORG_USER',
        permissionIds: [
            'organization:transactions:approve',
            'organization:transactions:reject',
            'organization:transactions:post',
            'organization:transactions:reverse',
        ],
        requiredModules: ['INVESTMENT', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.reports-reader': {
        id: 'organization.user.reports-reader',
        name: 'Organization User Reports Reader',
        persona: 'ORG_USER',
        permissionIds: [
            'organization:reports:read',
            'organization:holdings:read',
            'organization:reconciliation:read',
            'organization:corporate-actions:read',
        ],
        requiredModules: ['INVESTMENT', 'REPORTING', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.imports-operator': {
        id: 'organization.user.imports-operator',
        name: 'Organization User Imports Operator',
        persona: 'ORG_USER',
        permissionIds: [
            'organization:imports:list',
            'organization:imports:upload',
            'organization:imports:validate',
            'organization:imports:commit',
            'organization:imports:preview',
            'organization:imports:cancel',
            'organization:imports:retry',
        ],
        requiredModules: ['IMPORT', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.reconciliation-operator': {
        id: 'organization.user.reconciliation-operator',
        name: 'Organization User Reconciliation Operator',
        persona: 'ORG_USER',
        permissionIds: [
            'organization:reconciliation:list',
            'organization:reconciliation:read',
            'organization:reconciliation:create',
            'organization:reconciliation:match',
            'organization:reconciliation:resolve',
            'organization:reconciliation:complete',
            'organization:reconciliation:reopen',
            'organization:reconciliation:export',
        ],
        requiredModules: ['RECONCILIATION', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.corporate-actions-operator': {
        id: 'organization.user.corporate-actions-operator',
        name: 'Organization User Corporate Actions Operator',
        persona: 'ORG_USER',
        permissionIds: ['organization:corporate-actions:read', 'organization:corporate-actions:manage'],
        requiredModules: ['CORPORATE_ACTIONS', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.instruments-operator': {
        id: 'organization.user.instruments-operator',
        name: 'Organization User Instruments Operator',
        persona: 'ORG_USER',
        permissionIds: ['organization:instruments:read', 'organization:instruments:manage'],
        requiredModules: ['INSTRUMENTS', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.user.market-data-operator': {
        id: 'organization.user.market-data-operator',
        name: 'Organization User Market Data Operator',
        persona: 'ORG_USER',
        permissionIds: ['organization:market-data:read', 'organization:market-data:manage'],
        requiredModules: ['MARKET_DATA', 'DATA', 'DASHBOARD', 'NAVIGATION'],
    },
    'organization.client.base': {
        id: 'organization.client.base',
        name: 'Organization Client Base',
        persona: 'ORG_CLIENT',
        permissionIds: generatePermissionIdsForBundle('organization.client.base'),
        requiredModules: ['INVESTMENT', 'DASHBOARD', 'NAVIGATION'],
    },
    'client.holdings-reader': {
        id: 'client.holdings-reader',
        name: 'Client Holdings Reader',
        persona: 'ORG_CLIENT',
        permissionIds: [
            'client:dashboard:read',
            'client:holdings:list-own',
            'client:holdings:read-own',
        ],
        requiredModules: ['INVESTMENT', 'HOLDINGS', 'DASHBOARD', 'NAVIGATION'],
    },
    'client.transactions-reader': {
        id: 'client.transactions-reader',
        name: 'Client Transactions Reader',
        persona: 'ORG_CLIENT',
        permissionIds: [
            'client:dashboard:read',
            'client:transactions:list-own',
            'client:transactions:read-own',
        ],
        requiredModules: ['INVESTMENT', 'DASHBOARD', 'NAVIGATION'],
    },
    'client.reports-reader': {
        id: 'client.reports-reader',
        name: 'Client Reports Reader',
        persona: 'ORG_CLIENT',
        permissionIds: [
            'client:dashboard:read',
            'client:reports:list-own',
            'client:reports:read-own',
            'client:reports:export-own',
        ],
        requiredModules: ['INVESTMENT', 'REPORTING', 'DASHBOARD', 'NAVIGATION'],
    },
    'client.documents-reader': {
        id: 'client.documents-reader',
        name: 'Client Documents Reader',
        persona: 'ORG_CLIENT',
        permissionIds: ['client:documents:read-own'],
        requiredModules: ['DATA', 'DASHBOARD', 'NAVIGATION'],
    },
    'client.profile-updater': {
        id: 'client.profile-updater',
        name: 'Client Profile Updater',
        persona: 'ORG_CLIENT',
        permissionIds: [
            'client:profile:read',
            'client:profile:limited-update',
            'settings:view',
            'settings:profile',
            'settings:preferences',
            'settings:mfa',
            'settings:security',
            'settings:password:change',
            'settings:sessions',
        ],
        requiredModules: ['SETTINGS', 'DASHBOARD', 'NAVIGATION'],
    },
};
export function resolvePermissionBundle(bundleId) {
    return PERMISSION_BUNDLE_REGISTRY[bundleId];
}
